import {
    ITenantClosingDate,
    ITenantOpeningHours,
    ITenantTimeRange,
    TENANT_WEEKDAYS,
    TenantWeekday,
} from "../models/tenant";

/**
 * Opening-hours arithmetic, shared by the header status pill, the order
 * creation warning and the admin editor.
 *
 * Everything is evaluated in the tenant's IANA timezone rather than the
 * customer's device zone: a shop open until 18:00 in Paris must read "closed"
 * at 18:30 Paris time even for a customer whose laptop is set to Tunis.
 * `Intl.DateTimeFormat` does that conversion without pulling in a dayjs plugin.
 */

export const DEFAULT_TIMEZONE = "Europe/Paris";

const EMPTY_WEEK: Record<TenantWeekday, ITenantTimeRange[]> = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
};

/** Starting point offered in the admin editor: weekdays 8-12 / 13-18, weekend closed. */
export const DEFAULT_OPENING_HOURS: ITenantOpeningHours = {
    enabled: false,
    timezone: DEFAULT_TIMEZONE,
    week: {
        ...EMPTY_WEEK,
        monday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
        tuesday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
        wednesday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
        thursday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
        friday: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "18:00" }],
    },
};

export const emptyWeek = (): Record<TenantWeekday, ITenantTimeRange[]> => ({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
});

/** "08:30" -> 510 */
export const toMinutes = (time: string): number => {
    const [hours, minutes] = time.split(":");
    return Number(hours) * 60 + Number(minutes);
};

/** 510 -> "08:30" */
export const fromMinutes = (value: number): string => {
    const hours = Math.floor(value / 60) % 24;
    const minutes = value % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

export interface TenantNow {
    /** "YYYY-MM-DD" in the tenant timezone */
    date: string;
    weekday: TenantWeekday;
    /** Minutes since midnight in the tenant timezone */
    minutes: number;
}

const WEEKDAY_BY_INDEX: TenantWeekday[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

/**
 * Current wall-clock date, weekday and minute-of-day in `timezone`.
 * Falls back to the device zone if the timezone is unknown to the browser.
 */
export const getTenantNow = (timezone: string, now: Date = new Date()): TenantNow => {
    let parts: Intl.DateTimeFormatPart[];
    try {
        parts = new Intl.DateTimeFormat("en-US", {
            timeZone: timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            weekday: "short",
        }).formatToParts(now);
    } catch (error) {
        console.error("[openingHours] Unknown timezone, falling back to local:", timezone);
        parts = new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            weekday: "short",
        }).formatToParts(now);
    }

    const pick = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((part) => part.type === type)?.value ?? "";

    // hour12:false yields "24" instead of "00" at midnight in some engines.
    const hour = Number(pick("hour")) % 24;
    const weekdayShort = pick("weekday").toLowerCase();
    const weekday =
        WEEKDAY_BY_INDEX.find((day) => day.startsWith(weekdayShort)) ?? "monday";

    return {
        date: `${pick("year")}-${pick("month")}-${pick("day")}`,
        weekday,
        minutes: hour * 60 + Number(pick("minute")),
    };
};

/** Adds `days` to a "YYYY-MM-DD" string without touching timezones. */
export const addDays = (date: string, days: number): string => {
    const [year, month, day] = date.split("-").map(Number);
    const shifted = new Date(Date.UTC(year, month - 1, day + days));
    return shifted.toISOString().slice(0, 10);
};

const nextWeekday = (weekday: TenantWeekday, offset: number): TenantWeekday => {
    const index = TENANT_WEEKDAYS.indexOf(weekday);
    return TENANT_WEEKDAYS[(index + offset) % TENANT_WEEKDAYS.length];
};

export const findClosure = (
    closingDates: ITenantClosingDate[] | null | undefined,
    date: string,
): ITenantClosingDate | undefined =>
    (closingDates ?? []).find(
        (closure) => closure.startDate <= date && date <= closure.endDate,
    );

export type OpeningReason = "open" | "outside-hours" | "day-closed" | "holiday";

export interface OpeningStatus {
    isOpen: boolean;
    reason: OpeningReason;
    /** "HH:mm" the current opening window ends — only set when open. */
    closesAt?: string;
    /** Closure covering today — only set when `reason` is "holiday". */
    activeClosure?: ITenantClosingDate;
    /**
     * Next moment the tenant opens. `undefined` when nothing opens within the
     * next two weeks (a permanently empty schedule, or a very long closure).
     */
    nextOpening?: {
        weekday: TenantWeekday;
        date: string;
        time: string;
        /** True when that opening is later today. */
        isToday: boolean;
    };
}

/** How far ahead `getOpeningStatus` looks for the next opening slot. */
const LOOKAHEAD_DAYS = 14;

export const getOpeningStatus = (
    openingHours: ITenantOpeningHours | null | undefined,
    closingDates: ITenantClosingDate[] | null | undefined,
    now: Date = new Date(),
): OpeningStatus | null => {
    if (!openingHours?.enabled || !openingHours.week) {
        return null;
    }

    const tenantNow = getTenantNow(openingHours.timezone || DEFAULT_TIMEZONE, now);
    const todayClosure = findClosure(closingDates, tenantNow.date);
    const todayRanges = todayClosure ? [] : openingHours.week[tenantNow.weekday] ?? [];

    const current = todayRanges.find(
        (range) =>
            toMinutes(range.start) <= tenantNow.minutes &&
            tenantNow.minutes < toMinutes(range.end),
    );
    if (current) {
        return { isOpen: true, reason: "open", closesAt: current.end };
    }

    // Later today, then each following day until we find an opening.
    const laterToday = [...todayRanges]
        .sort((a, b) => toMinutes(a.start) - toMinutes(b.start))
        .find((range) => toMinutes(range.start) > tenantNow.minutes);

    let nextOpening: OpeningStatus["nextOpening"];
    if (laterToday) {
        nextOpening = {
            weekday: tenantNow.weekday,
            date: tenantNow.date,
            time: laterToday.start,
            isToday: true,
        };
    } else {
        for (let offset = 1; offset <= LOOKAHEAD_DAYS; offset++) {
            const date = addDays(tenantNow.date, offset);
            if (findClosure(closingDates, date)) {
                continue;
            }
            const weekday = nextWeekday(tenantNow.weekday, offset);
            const ranges = [...(openingHours.week[weekday] ?? [])].sort(
                (a, b) => toMinutes(a.start) - toMinutes(b.start),
            );
            if (ranges.length > 0) {
                nextOpening = { weekday, date, time: ranges[0].start, isToday: false };
                break;
            }
        }
    }

    let reason: OpeningReason = "outside-hours";
    if (todayClosure) {
        reason = "holiday";
    } else if (todayRanges.length === 0) {
        reason = "day-closed";
    }

    return { isOpen: false, reason, activeClosure: todayClosure, nextOpening };
};

/** Closures that have not finished yet, soonest first. */
export const upcomingClosures = (
    closingDates: ITenantClosingDate[] | null | undefined,
    today: string,
): ITenantClosingDate[] =>
    (closingDates ?? [])
        .filter((closure) => closure.endDate >= today)
        .sort((a, b) => a.startDate.localeCompare(b.startDate));

export type WeekValidationError =
    | { type: "invalidRange"; day: TenantWeekday }
    | { type: "overlappingRanges"; day: TenantWeekday };

/**
 * Same two cross-field rules the API enforces in `TenantsService`, so the admin
 * form can reject a bad schedule before it round-trips.
 */
export const validateWeek = (
    week: Record<TenantWeekday, ITenantTimeRange[]>,
): WeekValidationError[] => {
    const errors: WeekValidationError[] = [];
    for (const day of TENANT_WEEKDAYS) {
        const ranges = (week?.[day] ?? []).filter((range) => range?.start && range?.end);
        if (ranges.some((range) => toMinutes(range.end) <= toMinutes(range.start))) {
            errors.push({ type: "invalidRange", day });
            continue;
        }
        const sorted = [...ranges].sort((a, b) => toMinutes(a.start) - toMinutes(b.start));
        const overlaps = sorted.some(
            (range, index) =>
                index > 0 && toMinutes(range.start) < toMinutes(sorted[index - 1].end),
        );
        if (overlaps) {
            errors.push({ type: "overlappingRanges", day });
        }
    }
    return errors;
};
